'use client';

import React, { useEffect, useState } from 'react';
import { Plus, Copy, Mail } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

export function InviteButtonInjector() {
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteUrl, setInviteUrl] = useState('');
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const origin = window.location.origin;
      const path = window.location.pathname;
      setInviteUrl(`${origin}${path}`);
    }
  }, []);

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(inviteUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    const addTooltips = () => {
      // Adicionar tooltips para todos os botões
      const buttons = [
        { selector: 'button[data-lk-source="microphone"]', tooltip: 'Microfone' },
        { selector: 'button[data-lk-source="camera"]', tooltip: 'Câmera' },
        { selector: 'button[data-lk-source="screen_share"]', tooltip: 'Compartilhar tela' },
        { selector: '.lk-chat-toggle', tooltip: 'Chat' },
        { selector: 'button[data-hand-raise-button="true"]', tooltip: 'Levantar mão' },
        { selector: 'button[data-lk-source="settings"]', tooltip: 'Configurações' },
        { selector: 'button[data-lk-source="record"]', tooltip: 'Gravar' },
        { selector: '.lk-disconnect-button', tooltip: 'Sair' },
      ];

      buttons.forEach(({ selector, tooltip }) => {
        const button = document.querySelector(selector);
        if (button) {
          if (!button.getAttribute('data-tooltip')) {
            button.setAttribute('data-tooltip', tooltip);
          }
          // Remover title para evitar conflito com tooltip customizado
          if (button.hasAttribute('title')) {
            button.removeAttribute('title');
          }
        }
      });
    };

    const injectInviteButton = () => {
      const controlBar = document.querySelector('.lk-control-bar');
      if (!controlBar) return;

      // Verificar se o botão já foi injetado
      if (controlBar.querySelector('[data-invite-button="true"]')) return;

      // Encontrar o botão de levantar mão
      const handRaiseButton = controlBar.querySelector('[data-hand-raise-button="true"]');
      if (!handRaiseButton) return;

      // Criar o botão de convidar
      const inviteButton = document.createElement('button');
      inviteButton.setAttribute('data-invite-button', 'true');
      inviteButton.setAttribute('type', 'button');
      inviteButton.setAttribute('aria-pressed', 'false');
      inviteButton.setAttribute('data-tooltip', 'Convidar pessoas');
      inviteButton.removeAttribute('title'); // Remove any existing title attribute
      inviteButton.style.cssText = `
        width: 44px;
        height: 44px;
        padding: 0px;
        border-radius: 8px;
        background: var(--lk-bg2);
        color: var(--lk-fg);
        display: inline-flex;
        align-items: center;
        justify-content: center;
        border: none;
        cursor: pointer;
        transition: 0.2s;
        margin-left: 8px;
      `;

      // Criar o ícone SVG
      const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      svg.setAttribute('width', '20');
      svg.setAttribute('height', '20');
      svg.setAttribute('viewBox', '0 0 24 24');
      svg.setAttribute('fill', 'none');
      svg.setAttribute('stroke', 'currentColor');
      svg.setAttribute('stroke-width', '2.5');
      svg.setAttribute('stroke-linecap', 'round');
      svg.setAttribute('stroke-linejoin', 'round');
      svg.style.cssText = 'color: currentcolor; filter: none;';

      const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
      path.setAttribute('d', 'M12 5v14m-7-7h14');
      svg.appendChild(path);

      inviteButton.appendChild(svg);

      // Adicionar evento de clique
      inviteButton.addEventListener('click', () => {
        setInviteOpen(true);
      });

      // Garantir que o title seja sempre removido - abordagem mais agressiva
      const removeTitleFromButton = () => {
        const button = document.querySelector('[data-invite-button="true"]');
        if (button && button.hasAttribute('title')) {
          button.removeAttribute('title');
        }
      };

      // Remover title imediatamente e continuamente
      inviteButton.addEventListener('mouseenter', removeTitleFromButton);
      inviteButton.addEventListener('mouseover', removeTitleFromButton);
      inviteButton.addEventListener('focus', removeTitleFromButton);

      // Observer para monitorar mudanças no botão
      const buttonObserver = new MutationObserver(() => {
        removeTitleFromButton();
      });

      // Inserir após o botão de levantar mão
      handRaiseButton.parentNode?.insertBefore(inviteButton, handRaiseButton.nextSibling);

      // Iniciar observação do botão
      buttonObserver.observe(inviteButton, {
        attributes: true,
        attributeFilter: ['title'],
      });

      // Remover title periodicamente como backup
      const titleRemovalInterval = setInterval(removeTitleFromButton, 100);

      // Limpar interval após 10 segundos (backup)
      setTimeout(() => {
        clearInterval(titleRemovalInterval);
      }, 10000);
    };

    // Função específica para o botão de gravação
    const handleRecordButton = () => {
      const recordButton = document.querySelector('button[data-lk-source="record"]');
      if (recordButton) {
        // Garantir que tem data-tooltip
        if (!recordButton.getAttribute('data-tooltip')) {
          recordButton.setAttribute('data-tooltip', 'Gravar');
        }
        // Remover title sempre
        recordButton.removeAttribute('title');

        // Adicionar event listeners para remover title continuamente
        const removeTitle = () => recordButton.removeAttribute('title');
        recordButton.addEventListener('mouseenter', removeTitle);
        recordButton.addEventListener('mouseover', removeTitle);
        recordButton.addEventListener('focus', removeTitle);
      }
    };

    // Função específica para o botão de sair
    const handleDisconnectButton = () => {
      const disconnectButton = document.querySelector('.lk-disconnect-button');
      if (disconnectButton) {
        // Garantir que tem data-tooltip
        if (!disconnectButton.getAttribute('data-tooltip')) {
          disconnectButton.setAttribute('data-tooltip', 'Sair');
        }
        // Remover title sempre
        disconnectButton.removeAttribute('title');

        // Adicionar event listeners para remover title continuamente
        const removeTitle = () => disconnectButton.removeAttribute('title');
        disconnectButton.addEventListener('mouseenter', removeTitle);
        disconnectButton.addEventListener('mouseover', removeTitle);
        disconnectButton.addEventListener('focus', removeTitle);
      }
    };

    // Tentar injetar imediatamente
    addTooltips();
    injectInviteButton();
    handleRecordButton(); // Tratar botão de gravação imediatamente
    handleDisconnectButton(); // Tratar botão de sair imediatamente

    // Observar só a barra de controles (não document.body inteiro)
    const observeTarget =
      document.querySelector('.lk-control-bar') ??
      document.querySelector('.lk-room-container') ??
      document.body;

    const observer = new MutationObserver(() => {
      addTooltips();
      injectInviteButton();
      handleRecordButton();
      handleDisconnectButton();
    });

    observer.observe(observeTarget, {
      childList: true,
      subtree: true,
    });

    return () => {
      observer.disconnect();
    };
  }, []);

  return (
    <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
      <DialogContent className="max-w-md bg-neutral-800 border-neutral-700 text-white">
        <DialogHeader>
          <DialogTitle className="text-white">Convide mais pessoas</DialogTitle>
          <DialogDescription className="text-neutral-300">
            Compartilhando o link da reunião
          </DialogDescription>
        </DialogHeader>

        <div className="flex items-center gap-2">
          <Input
            readOnly
            value={inviteUrl}
            className="flex-1 bg-neutral-700 border-neutral-600 text-white"
          />
          <Button
            onClick={handleCopyLink}
            variant="secondary"
            className="shrink-0 bg-blue-500 hover:bg-blue-600"
          >
            <Copy className="w-4 h-4 mr-1" />
            {copied ? 'Copiado' : 'Copiar'}
          </Button>
        </div>

        <div className="mt-4">
          <p className="text-sm text-neutral-300 mb-3">Compartilhar convite da reunião</p>
          <div className="flex items-center gap-3">
            <a
              href={`mailto:?subject=${encodeURIComponent('Convite para reunião')}&body=${encodeURIComponent('Entre na reunião: ' + inviteUrl)}`}
              target="_blank"
              rel="noreferrer"
            >
              <Button
                variant="outline"
                size="icon"
                title="E-mail padrão"
                className="border-neutral-600 text-white hover:bg-neutral-700"
              >
                <Mail className="w-4 h-4" />
              </Button>
            </a>
            <a
              href={`https://mail.google.com/mail/?view=cm&fs=1&su=${encodeURIComponent('Convite para reunião')}&body=${encodeURIComponent('Entre na reunião: ' + inviteUrl)}`}
              target="_blank"
              rel="noreferrer"
            >
              <Button
                variant="outline"
                size="icon"
                title="Gmail"
                className="border-neutral-600 text-white hover:bg-neutral-700"
              >
                G
              </Button>
            </a>
            <a
              href={`https://outlook.live.com/mail/deeplink/compose?subject=${encodeURIComponent('Convite para reunião')}&body=${encodeURIComponent('Entre na reunião: ' + inviteUrl)}`}
              target="_blank"
              rel="noreferrer"
            >
              <Button
                variant="outline"
                size="icon"
                title="Outlook"
                className="border-neutral-600 text-white hover:bg-neutral-700"
              >
                O
              </Button>
            </a>
            <a
              href={`https://compose.mail.yahoo.com/?subject=${encodeURIComponent('Convite para reunião')}&body=${encodeURIComponent('Entre na reunião: ' + inviteUrl)}`}
              target="_blank"
              rel="noreferrer"
            >
              <Button
                variant="outline"
                size="icon"
                title="Yahoo Mail"
                className="border-neutral-600 text-white hover:bg-neutral-700"
              >
                Y
              </Button>
            </a>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
